var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import DataTable from '@/components/data-table/DataTable';
import PageHeader from '@/components/page-header/PageHeader';
import { RolesEnum } from '@/items/Roles';
import { Group } from '@/models/Group';
import { GroupType } from '@/models/GroupType';
import ErrorHandler from '@/support/ErrorHandler';
import CreateDialog from '@/views/Groups/CreateDialog';
import { Component, Watch } from 'vue-property-decorator';
import AbstractUrl from '@/support/AbstractUrl';
import { Teacher } from '@/models/Teacher';
let Groups = class Groups extends AbstractUrl {
    constructor() {
        super(...arguments);
        this.groupTypes = [];
        this.isLoadingGroupTypes = true;
        this.$pageTitle = 'Groups';
        this.selected = [];
        this.selectedGroup = null;
        this.isCreating = false;
        this.showDeletedGroups = false;
        this.selectedGroupTypes = [];
        this.teacher = null;
    }
    mounted() {
        this.intialize();
    }
    async intialize() {
        this.isLoadingGroupTypes = true;
        this.groupTypes = await new GroupType().include(['name', 'label']).all();
        if (this.showFavoriteGroupColumn) {
            await this.fetchTeacher();
        }
        this.isLoadingGroupTypes = false;
    }
    get isTeacher() {
        // Only teachers and roots
        return this.user && this.user.hasRole([RolesEnum.TEACHER, RolesEnum.ROOT]);
    }
    refreshDataTable() {
        document.dispatchEvent(new CustomEvent('datatable:hook', {
            detail: (dataTable) => {
                dataTable.refresh();
            },
        }));
    }
    toggleDeletedGroups() {
        if (this.showDeletedGroups) {
            this.showDeletedGroups = false;
            return;
        }
        this.showDeletedGroups = true;
    }
    // We fetch the teacher to retrieve his/her favourite group
    async fetchTeacher() {
        try {
            this.teacher = await new Teacher().include('teacher').find(this.user.id);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async updateTeacherFavouriteGroup(group) {
        try {
            await this.user.update({
                favourite_group: group.id,
            });
            await this.fetchTeacher();
            this.refreshDataTable();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async restoreGroup(group) {
        await group.restoreGroup();
        this.refreshDataTable();
    }
    get asyncAppendIcon() {
        if (this.selectedGroupTypes.length) {
            return 'close';
        }
        return 'filter_list';
    }
    get user() {
        return this.$store.state.Auth;
    }
    get isTeacherWithExpertRights() {
        return this.user && this.user.isTeacherWithExpertRights();
    }
    get tableOptions() {
        return {
            name: {
                singular: this.$tc('group.title', 1),
                plural: this.$tc('group.title', 2),
            },
            headers: [
                {
                    text: this.$t('name.title'),
                    value: 'name',
                    action: this.showDeletedGroups ? undefined : '/groups/{id}',
                },
                {
                    text: this.$tc('group.type'),
                    value: 'type',
                    transform: (type, group) => type.name,
                },
                {
                    text: this.$tc('group.amountOfStudents'),
                    value: 'meta',
                    transform: (type, any) => {
                        return type.totals.students;
                    },
                },
            ],
            actions: this.showDeletedGroups ? this.deletedGroupTableActions : this.groupTableActions,
            filters: [],
            model: this.groupModel,
        };
    }
    get groupTableActions() {
        const actions = [
            {
                type: 'view',
                label: 'view',
                icon: 'remove_red_eye',
                action: '/groups/{id}',
            },
            {
                type: 'delete',
                label: 'delete',
                icon: 'delete',
                action: (vm, group) => {
                    this.selectedGroup = group;
                    this.$store.dispatch('openDialog', this.dialogOptions);
                },
                roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.TEACHER],
            },
        ];
        // Teacher can only favourite groups on "my groups" page
        if (this.showFavoriteGroupColumn) {
            actions.splice(0, 0, {
                type: 'favourite',
                label: 'favourite',
                icon: (_vm, group) => {
                    if (this.teacher &&
                        this.teacher.teacher &&
                        this.teacher.teacher.favourite_group_id === group.id) {
                        return 'star';
                    }
                    return 'star_border';
                },
                action: (vm, group) => {
                    this.updateTeacherFavouriteGroup(group);
                },
                roles: [RolesEnum.ROOT, RolesEnum.TEACHER],
            });
        }
        return actions;
    }
    get deletedGroupTableActions() {
        return [
            {
                type: 'restore group',
                text: this.$tc('user.restore'),
                icon: 'unarchive',
                action: (vm, group) => {
                    this.restoreGroup(group);
                },
                roles: [RolesEnum.ADMIN, RolesEnum.ROOT, RolesEnum.TRAINER, RolesEnum.MANAGER],
            },
        ];
    }
    get showFavoriteGroupColumn() {
        return this.isTeacher && this.$route.name === 'mine';
    }
    get groupModel() {
        let filters = {};
        if (this.selectedGroupTypes.length) {
            filters.types = this.selectedGroupTypes;
        }
        if (this.showDeletedGroups) {
            filters = { ...filters, ...{ only_trashed: 1 } };
            return new Group().include(['roles', 'projects', 'type', 'meta']).filter(filters);
        }
        if (this.$route.meta && this.$route.meta.myGroup) {
            filters = { ...filters, ...{ users: this.$store.state.Auth.id } };
            return new Group().filter(filters).include(['roles', 'projects', 'type', 'meta']);
        }
        return new Group().filter(filters).include(['roles', 'projects', 'type', 'meta']);
    }
    get dialogOptions() {
        const group = this.selectedGroup;
        return {
            title: group.name || this.$t('dialogOptions.confirmation').toString(),
            text: this.$tc('dialogOptions.delete', 2, {
                item: this.$t('dialogOptions.item.group'),
            }).toString(),
            type: 'warning',
            buttons: {
                confirm: {
                    text: this.$t('dialogOptions.button.delete').toString(),
                    action: () => {
                        if (!group) {
                            return;
                        }
                        group
                            .delete()
                            .then((response) => {
                            if (response) {
                                this.selectedGroup = null;
                                this.refreshDataTable();
                            }
                        })
                            .catch((error) => {
                            ErrorHandler.network(error);
                        });
                    },
                },
                cancel: {
                    text: this.$t('dialogOptions.button.cancel').toString(),
                    action: () => {
                        this.selectedGroup = null;
                    },
                },
            },
        };
    }
    get canCreateGroups() {
        return (this.$store.state.Auth &&
            this.$store.state.Auth.hasRole([
                RolesEnum.TEACHER,
                RolesEnum.SUPERVISOR,
                RolesEnum.ADMIN,
                RolesEnum.ROOT,
                RolesEnum.TRAINER,
                RolesEnum.MANAGER,
            ]));
    }
    selectedGroupTypesChanged() {
        if (!this.selectedGroupTypes.length) {
            this.clearQueryParams();
        }
        else {
            this.setQueryParams({ types: this.selectedGroupTypes });
        }
    }
};
__decorate([
    Watch('selectedGroupTypes'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Groups.prototype, "selectedGroupTypesChanged", null);
Groups = __decorate([
    Component({
        components: {
            DataTable,
            PageHeader,
            CreateDialog,
        },
    })
], Groups);
export default Groups;
